/**
 * EquipmentStatePanel — an equipment's operational state timeline (CAP-UI-01).
 *
 * Renders the ordered state transitions from `equipment_state` (migration 0136):
 * the current state as a badge, then each transition (from -> to) with its honest
 * signal source and observed time. When nothing is captured it shows an honest
 * empty state, never a fabricated one, with a one-click Backfill that derives the
 * timeline from the equipment's existing heat_events.
 *
 * Read-only for everyone; the Backfill action is idempotent and org-scoped.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, History } from "lucide-react";

import {
  twinEquipment,
  type EquipmentResolutionBasis,
  type EquipmentSignalSource,
} from "@/lib/api/twin-equipment";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/ui/card";

const SOURCE_LABEL: Record<EquipmentSignalSource, string> = {
  coil_register: "coil/register",
  analog: "analog",
  weigh_count: "weigh/count",
  instrument: "instrument",
  human: "human",
  derived: "derived",
  unconfirmed: "unconfirmed",
  unset: "unset",
};

// How the state was resolved. entity_view (the declared per-entity transition) is the norm and
// shown unadorned; the rest are less precise and worth flagging so a coarse or guarded resolution
// never reads as certain as a declared one.
const RESOLUTION_LABEL: Record<EquipmentResolutionBasis, string> = {
  entity_view: "declared",
  global_fallback: "coarse fallback",
  guarded_unresolved: "guarded",
  reconciled: "reconciled",
};

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function EquipmentStatePanel({ entityId }: { entityId: string | null }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["equipment-state", entityId],
    queryFn: () => twinEquipment.state(entityId as string),
    enabled: !!entityId,
    // Tick live so the current state + timeline advance as the driver (or a real
    // gateway) walks the FSM over the wire. react-query pauses this while the tab
    // is hidden, so it is cheap.
    refetchInterval: 5000,
  });
  const backfill = useMutation({
    mutationFn: () => twinEquipment.backfillState(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["equipment-state"] }),
  });

  if (!entityId) return null;
  const data = q.data;
  const rows = data?.timeline ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2">
        <Activity className="size-5 text-brand" />
        <CardTitle className="flex-1 text-sm">Operational state</CardTitle>
        {data?.current ? (
          <Badge className="capitalize">{data.current.to_state}</Badge>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {q.isLoading ? (
          <p className="text-sm text-on-surface-muted">Loading state timeline…</p>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-start gap-3 rounded-md border border-dashed border-border p-4">
            <div className="flex items-center gap-2 text-sm text-on-surface-muted">
              <History className="size-4" />
              No state captured yet.
            </div>
            <p className="text-xs text-on-surface-muted">
              An equipment&apos;s states come from its witnessed events. This
              machine has none recorded. Derive its timeline from the heats it
              already has:
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => backfill.mutate()}
              disabled={backfill.isPending}
            >
              {backfill.isPending ? "Backfilling…" : "Backfill from history"}
            </Button>
            {backfill.data ? (
              <p className="text-xs text-on-surface-muted">
                Wrote {backfill.data.written} transition
                {backfill.data.written === 1 ? "" : "s"} across the plant.
              </p>
            ) : null}
          </div>
        ) : (
          <ol className="space-y-1.5">
            {rows.map((r) => (
              <li
                key={r.id}
                className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border/60 pb-1.5 text-sm last:border-0"
              >
                <span className="font-medium capitalize">{r.to_state}</span>
                {r.from_state ? (
                  <span className="text-xs text-on-surface-muted">
                    from {r.from_state}
                  </span>
                ) : null}
                <Badge className="text-[10px]" title="how this transition was observed">
                  {SOURCE_LABEL[r.source] ?? r.source}
                </Badge>
                {r.data_class !== "real" ? (
                  <Badge
                    className="text-[10px] opacity-70"
                    title="provenance of this transition"
                  >
                    {r.data_class}
                  </Badge>
                ) : null}
                {r.resolution_basis !== "entity_view" ? (
                  <Badge
                    className="text-[10px] opacity-70"
                    title="how this state was resolved (a declared per-entity transition is the most precise)"
                  >
                    {RESOLUTION_LABEL[r.resolution_basis]}
                  </Badge>
                ) : null}
                {r.heat_id === null ? (
                  <span
                    className="text-[10px] text-on-surface-muted"
                    title="a transition recorded outside any heat"
                  >
                    no heat
                  </span>
                ) : null}
                <span className="ml-auto text-xs tabular-nums text-on-surface-muted">
                  {when(r.observed_at)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
